import {
  absBig,
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  formatBig,
  gcdBig,
  isqrtBig,
} from "@/lib/tool-kit/math/bigint";
import { padRange, type Viewport } from "@/lib/tool-kit/math/plot";
import {
  formatRadicalFraction,
  type RadicalFraction,
  type SimplifiedRoot,
  simplifySqrt,
  sqrtOfFraction,
} from "@/lib/tool-kit/math/radical";
import {
  addR,
  commonDenominator,
  divR,
  formatFraction,
  formatPolynomial,
  formatRationalNice,
  fromBigInt,
  intR,
  isIntegerR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * ax² + bx + c = 0, solved exactly.
 *
 * The coefficients are rationals, so 0.5x² − 1.5x + 1 = 0 is solved as written rather
 * than through floats. When a coefficient is not a whole number, the equation is first
 * multiplied through by the common denominator (and divided by any common factor) — the
 * same roots, and whole numbers make the radical form readable: x = (3 ± √17)/4 rather
 * than a fraction inside a fraction under a root.
 */

export type QuadraticKind =
  | "two-rational"
  | "two-irrational"
  | "repeated"
  | "complex";

/** (p ± q√m)/s, or (p ± q·i√m)/s when complex. Reduced, with s > 0 and q > 0. */
export interface SurdRoots {
  p: bigint;
  q: bigint;
  m: bigint;
  s: bigint;
  complex: boolean;
}

export interface QuadraticSolution {
  a: Rational;
  b: Rational;
  c: Rational;
  /** The whole-number equation actually solved: A x² + B x + C = 0. */
  A: bigint;
  B: bigint;
  C: bigint;
  /** What the original was multiplied by to get A, B, C (1 when already whole). */
  multiplier: Rational;
  /** b² − 4ac of the equation as entered. */
  discriminant: Rational;
  /** B² − 4AC of the whole-number equation. */
  integerDiscriminant: bigint;
  /** √|B² − 4AC| in simplest radical form. */
  root: SimplifiedRoot;
  kind: QuadraticKind;
  /** Exact roots when rational: ascending, one entry for a repeated root. */
  rationalRoots: Rational[] | null;
  /** The radical form when the roots are irrational or complex. */
  surd: SurdRoots | null;
  /** Real roots ascending, or the complex pair as re ± im·i. */
  decimals: { re: number; im: number }[];
  vertex: { x: Rational; y: Rational };
  /** k(q₁x − p₁)(q₂x − p₂), when the roots are rational. */
  factored: { k: Rational; factors: { q: bigint; p: bigint }[] } | null;
  /** −b/a and c/a — Vieta's formulas, a check on any pair of roots. */
  sum: Rational;
  product: Rational;
}

function integerForm(a: Rational, b: Rational, c: Rational) {
  if (isIntegerR(a) && isIntegerR(b) && isIntegerR(c)) {
    return { A: a.num, B: b.num, C: c.num, multiplier: intR(1) };
  }
  const lcd = commonDenominator([a, b, c]);
  const A0 = (a.num * lcd) / a.den;
  const B0 = (b.num * lcd) / b.den;
  const C0 = (c.num * lcd) / c.den;
  const common = gcdBig(gcdBig(A0, B0), C0);
  return {
    A: A0 / common,
    B: B0 / common,
    C: C0 / common,
    multiplier: divR(fromBigInt(lcd), fromBigInt(common)),
  };
}

function reduceSurd(
  p: bigint,
  q: bigint,
  m: bigint,
  s: bigint,
  complex: boolean
): SurdRoots {
  const common = gcdBig(gcdBig(p, q), s);
  const sign = s < BIG_ZERO ? -BIG_ONE : BIG_ONE;
  return {
    p: (sign * p) / common,
    q: absBig(q) / common,
    m,
    s: (sign * s) / common,
    complex,
  };
}

/** Real roots by the cancellation-free form of the formula. */
function realDecimals(A: number, B: number, C: number, D: number) {
  const sqrtD = Math.sqrt(D);
  if (B === 0) {
    const x = sqrtD / (2 * Math.abs(A));
    return [-x, x];
  }
  const q = -(B + Math.sign(B) * sqrtD) / 2;
  const roots = [q / A, C / q];
  return roots.sort((x, y) => x - y);
}

function classify(D: bigint): QuadraticKind {
  if (D === BIG_ZERO) {
    return "repeated";
  }
  if (D < BIG_ZERO) {
    return "complex";
  }
  const s = isqrtBig(D);
  return s * s === D ? "two-rational" : "two-irrational";
}

function rationalRootsOf(A: bigint, B: bigint, D: bigint): Rational[] {
  const s = isqrtBig(D);
  const twoA = BIG_TWO * A;
  const first = divR(fromBigInt(-B - s), fromBigInt(twoA));
  const second = divR(fromBigInt(-B + s), fromBigInt(twoA));
  if (D === BIG_ZERO) {
    return [first];
  }
  return signR(subR(first, second)) < 0 ? [first, second] : [second, first];
}

function factoredForm(a: Rational, roots: Rational[]) {
  const pair = roots.length === 1 ? [roots[0], roots[0]] : roots;
  const factors = pair.map((r) => ({ q: r.den, p: r.num }));
  const k = divR(a, fromBigInt(factors[0].q * factors[1].q));
  return { k, factors };
}

function decimalsOf(
  kind: QuadraticKind,
  A: bigint,
  B: bigint,
  C: bigint,
  D: bigint,
  rationalRoots: Rational[] | null
) {
  if (rationalRoots) {
    return rationalRoots.map((r) => ({ re: toNumberR(r), im: 0 }));
  }
  const An = Number(A);
  const Bn = Number(B);
  if (kind === "complex") {
    const re = -Bn / (2 * An);
    const im = Math.sqrt(-Number(D)) / (2 * Math.abs(An));
    return [
      { re, im: -im },
      { re, im },
    ];
  }
  return realDecimals(An, Bn, Number(C), Number(D)).map((re) => ({
    re,
    im: 0,
  }));
}

/** Null when a = 0 — the equation is linear, not quadratic. */
export function solveQuadratic(
  a: Rational,
  b: Rational,
  c: Rational
): QuadraticSolution | null {
  if (isZeroR(a)) {
    return null;
  }
  const { A, B, C, multiplier } = integerForm(a, b, c);
  const D = B * B - BigInt(4) * A * C;
  const kind = classify(D);
  const root = simplifySqrt(absBig(D));
  const rationalRoots =
    kind === "two-rational" || kind === "repeated"
      ? rationalRootsOf(A, B, D)
      : null;
  const surd = rationalRoots
    ? null
    : reduceSurd(
        -B,
        root.coefficient,
        root.radicand,
        BIG_TWO * A,
        kind === "complex"
      );
  const twoA = mulR(intR(2), a);
  const vertexX = divR(negR(b), twoA);
  const vertexY = subR(c, divR(mulR(b, b), mulR(intR(4), a)));
  return {
    a,
    b,
    c,
    A,
    B,
    C,
    multiplier,
    discriminant: subR(mulR(b, b), mulR(intR(4), mulR(a, c))),
    integerDiscriminant: D,
    root,
    kind,
    rationalRoots,
    surd,
    decimals: decimalsOf(kind, A, B, C, D, rationalRoots),
    vertex: { x: vertexX, y: vertexY },
    factored: rationalRoots ? factoredForm(a, rationalRoots) : null,
    sum: divR(negR(b), a),
    product: divR(c, a),
  };
}

/** bx + c = 0 when a = 0: one root, no root (c ≠ 0), or every x (c = 0). */
export type LinearSolution =
  | { kind: "one"; root: Rational }
  | { kind: "none" }
  | { kind: "all" };

export function solveLinear(b: Rational, c: Rational): LinearSolution {
  if (!isZeroR(b)) {
    return { kind: "one", root: divR(negR(c), b) };
  }
  return isZeroR(c) ? { kind: "all" } : { kind: "none" };
}

/* ------------------------------------------------- completing the square */

export interface CompletingSquare {
  /** b/a and c/a: the equation divided by a. */
  bOverA: Rational;
  cOverA: Rational;
  /** b/(2a), the number inside the square, and its square. */
  half: Rational;
  halfSquared: Rational;
  /** −c/a + (b/2a)², which equals (b² − 4ac)/(4a²). */
  right: Rational;
  /** √right in simplest form, when right ≥ 0; √(−right) otherwise. */
  rootOfRight: RadicalFraction;
}

export function completeTheSquare(
  solution: QuadraticSolution
): CompletingSquare {
  const { a, b, c } = solution;
  const bOverA = divR(b, a);
  const cOverA = divR(c, a);
  const half = divR(b, mulR(intR(2), a));
  const halfSquared = mulR(half, half);
  const right = addR(negR(cOverA), halfSquared);
  const magnitude = signR(right) < 0 ? negR(right) : right;
  return {
    bOverA,
    cOverA,
    half,
    halfSquared,
    right,
    rootOfRight: sqrtOfFraction(magnitude.num, magnitude.den),
  };
}

/* -------------------------------------------------------------- notation */

/** The part after ±: "2√3", "√3", "2i", "i√3", "4". */
export function surdTerm(q: bigint, m: bigint, complex: boolean): string {
  const front = q === BIG_ONE && (complex || m !== BIG_ONE) ? "" : q.toString();
  const imaginary = complex ? "i" : "";
  const radical = m === BIG_ONE ? "" : `√${m.toString()}`;
  return `${front}${imaginary}${radical}`;
}

/** "(3 ± √17)/4", "−1 ± 2i", "±√2", "±√3/2". */
export function formatSurdPair(surd: SurdRoots): string {
  const term = surdTerm(surd.q, surd.m, surd.complex);
  const denominator = surd.s === BIG_ONE ? "" : `/${surd.s.toString()}`;
  if (surd.p === BIG_ZERO) {
    return `±${term}${denominator}`;
  }
  const numerator = `${formatBig(surd.p)} ± ${term}`;
  return surd.s === BIG_ONE ? numerator : `(${numerator})${denominator}`;
}

/** One root of the pair: sign +1 → "(3 + √17)/4", −1 → "(3 − √17)/4". */
export function formatSurdRoot(surd: SurdRoots, sign: 1 | -1): string {
  const term = surdTerm(surd.q, surd.m, surd.complex);
  const denominator = surd.s === BIG_ONE ? "" : `/${surd.s.toString()}`;
  if (surd.p === BIG_ZERO) {
    const lead = sign === 1 ? "" : "−";
    return surd.s === BIG_ONE
      ? `${lead}${term}`
      : `${lead}${term}${denominator}`;
  }
  const numerator = `${formatBig(surd.p)} ${sign === 1 ? "+" : "−"} ${term}`;
  return surd.s === BIG_ONE ? numerator : `(${numerator})${denominator}`;
}

/** "2x − 3", "x + 1", "x", "3x" for the factor q·x − p. */
export function formatLinearFactor(q: bigint, p: bigint): string {
  const xTerm = q === BIG_ONE ? "x" : `${q.toString()}x`;
  if (p === BIG_ZERO) {
    return xTerm;
  }
  return p > BIG_ZERO
    ? `${xTerm} − ${p.toString()}`
    : `${xTerm} + ${(-p).toString()}`;
}

/** "2(x − 3)(x + 1)", "(2x + 1)(3x − 2)", "−(x − 2)²", "x(x − 5)", "0.5(x − 1)(x − 2)". */
export function formatFactored(
  factored: NonNullable<QuadraticSolution["factored"]>,
  decimalMark = "."
): string {
  const [first, second] = factored.factors;
  const wrap = (q: bigint, p: bigint) =>
    p === BIG_ZERO ? formatLinearFactor(q, p) : `(${formatLinearFactor(q, p)})`;
  const same = first.q === second.q && first.p === second.p;
  const body = same
    ? `${wrap(first.q, first.p)}²`
    : `${wrap(first.q, first.p)}${wrap(second.q, second.p)}`;
  const k = factored.k;
  if (k.num === BIG_ONE && k.den === BIG_ONE) {
    return body;
  }
  if (k.num === -BIG_ONE && k.den === BIG_ONE) {
    return `−${body}`;
  }
  const lead = formatRationalNice(k, decimalMark);
  // A fraction in front reads as part of the first factor without brackets.
  return lead.includes("/") ? `(${lead})${body}` : `${lead}${body}`;
}

/** "2x² − 3x − 5" from whole-number coefficients. */
export function formatIntegerQuadratic(
  A: bigint,
  B: bigint,
  C: bigint
): string {
  const terms: string[] = [];
  const lead = absBig(A) === BIG_ONE ? "" : absBig(A).toString();
  terms.push(`${A < BIG_ZERO ? "−" : ""}${lead}x²`);
  if (B !== BIG_ZERO) {
    const size = absBig(B) === BIG_ONE ? "" : absBig(B).toString();
    terms.push(`${B < BIG_ZERO ? "−" : "+"} ${size}x`);
  }
  if (C !== BIG_ZERO) {
    terms.push(`${C < BIG_ZERO ? "−" : "+"} ${absBig(C).toString()}`);
  }
  return terms.join(" ");
}

/* ----------------------------------------------------- working, as text */

/** "2x² − 3x − 5" — the left side of the equation as entered. */
export function formatQuadraticExpression(
  a: Rational,
  b: Rational,
  c: Rational,
  style: NumberStyle = "fraction",
  decimalMark = "."
): string {
  return formatPolynomial(
    [
      { coefficient: a, variable: "x²" },
      { coefficient: b, variable: "x" },
      { coefficient: c, variable: "" },
    ],
    style,
    decimalMark
  );
}

function bracketNegative(n: bigint): string {
  return n < BIG_ZERO ? `(${formatBig(n)})` : formatBig(n);
}

/** "(−3)² − 4 × 2 × (−5) = 9 − (−40)" — b² − 4ac with the numbers in. */
export function discriminantWorking(A: bigint, B: bigint, C: bigint): string {
  const fourAC = BigInt(4) * A * C;
  return `${bracketNegative(B)}² − 4 × ${bracketNegative(A)} × ${bracketNegative(C)} = ${formatBig(B * B)} − ${bracketNegative(fourAC)}`;
}

function rootOfDiscriminant(D: bigint): string {
  return D < BIG_ZERO ? `√(${formatBig(D)})` : `√${D.toString()}`;
}

/** "(3 ± √49) / 4" — the formula with the numbers substituted. */
export function formulaSubstitution(A: bigint, B: bigint, D: bigint): string {
  return `(${formatBig(-B)} ± ${rootOfDiscriminant(D)}) / ${bracketNegative(BIG_TWO * A)}`;
}

/** "√8 = 2√2", "√(−16) = 4i", "√(−3) = i√3", "√49 = 7". */
export function rootSimplification(D: bigint, root: SimplifiedRoot): string {
  const value =
    D < BIG_ZERO
      ? surdTerm(root.coefficient, root.radicand, true)
      : surdTerm(root.coefficient, root.radicand, false);
  return `${rootOfDiscriminant(D)} = ${value}`;
}

/** "(2 ± 2√2) / 2" — the roots before common factors are cancelled. */
export function unreducedSurd(solution: QuadraticSolution): string {
  const term = surdTerm(
    solution.root.coefficient,
    solution.root.radicand,
    solution.kind === "complex"
  );
  return `(${formatBig(-solution.B)} ± ${term}) / ${bracketNegative(BIG_TWO * solution.A)}`;
}

/** ["(3 + 7) / 4 = 5/2", "(3 − 7) / 4 = −1"] for rational roots. */
export function rationalRootWorking(solution: QuadraticSolution): string[] {
  const s = isqrtBig(solution.integerDiscriminant);
  const minusB = -solution.B;
  const twoA = BIG_TWO * solution.A;
  const value = (n: bigint) =>
    formatFraction(divR(fromBigInt(n), fromBigInt(twoA)));
  if (s === BIG_ZERO) {
    return [
      `${formatBig(minusB)} / ${bracketNegative(twoA)} = ${value(minusB)}`,
    ];
  }
  return [
    `(${formatBig(minusB)} + ${s}) / ${bracketNegative(twoA)} = ${value(minusB + s)}`,
    `(${formatBig(minusB)} − ${s}) / ${bracketNegative(twoA)} = ${value(minusB - s)}`,
  ];
}

/** √(negative) as text: "2i", "i√3/2", "3i√2/4". */
export function imaginaryRadical(root: RadicalFraction): string {
  const front = root.coefficient === BIG_ONE ? "" : root.coefficient.toString();
  const radical = root.radicand === BIG_ONE ? "" : `√${root.radicand}`;
  const denominator =
    root.denominator === BIG_ONE ? "" : `/${root.denominator}`;
  return `${front}i${radical}${denominator}`;
}

export interface SquareWorking {
  monic: string;
  movedLeft: string;
  movedRight: string;
  addedLeft: string;
  half: string;
  halfSquared: string;
  right: string;
  square: string;
  shifted: string;
  /** "7/4", "√17/4", "i√3/2", or "0". */
  root: string;
  /** "3/4 ± 7/4", "±√2". */
  solution: string;
  rightSign: number;
}

/** Every line of completing the square, as exact fractions. */
export function squareWorking(solution: QuadraticSolution): SquareWorking {
  const steps = completeTheSquare(solution);
  const one = intR(1);
  const xPart = [
    { coefficient: one, variable: "x²" },
    { coefficient: steps.bOverA, variable: "x" },
  ];
  const shifted = formatPolynomial([
    { coefficient: one, variable: "x" },
    { coefficient: steps.half, variable: "" },
  ]);
  const rightSign = signR(steps.right);
  let root = "0";
  if (rightSign > 0) {
    root = formatRadicalFraction(steps.rootOfRight);
  } else if (rightSign < 0) {
    root = imaginaryRadical(steps.rootOfRight);
  }
  const centre = negR(steps.half);
  return {
    monic: formatPolynomial([
      ...xPart,
      { coefficient: steps.cOverA, variable: "" },
    ]),
    movedLeft: formatPolynomial(xPart),
    movedRight: formatFraction(negR(steps.cOverA)),
    addedLeft: formatPolynomial([
      ...xPart,
      { coefficient: steps.halfSquared, variable: "" },
    ]),
    half: formatFraction(steps.half),
    halfSquared: formatFraction(steps.halfSquared),
    right: formatFraction(steps.right),
    square: isZeroR(steps.half) ? "x²" : `(${shifted})²`,
    shifted,
    root,
    solution: isZeroR(centre)
      ? `±${root}`
      : `${formatFraction(centre)} ± ${root}`,
    rightSign,
  };
}

/* ----------------------------------------------------------------- graph */

/** The value of ax² + bx + c at x, in floats, for drawing. */
export function quadraticAt(
  solution: QuadraticSolution
): (x: number) => number {
  const a = toNumberR(solution.a);
  const b = toNumberR(solution.b);
  const c = toNumberR(solution.c);
  return (x) => (a * x + b) * x + c;
}

/**
 * A window that shows what matters: both x-intercepts with room either side, or — with
 * one or no real root — the vertex with the arms rising clearly away from it. The
 * x-axis is always included, so a parabola that misses it visibly misses it.
 */
export function graphWindow(solution: QuadraticSolution): Viewport {
  const f = quadraticAt(solution);
  const h = toNumberR(solution.vertex.x);
  const k = toNumberR(solution.vertex.y);
  const real =
    solution.kind === "complex" ? [] : solution.decimals.map((d) => d.re);
  let xMin: number;
  let xMax: number;
  const span = real.length === 2 ? real[1] - real[0] : 0;
  if (span > 0) {
    xMin = real[0] - 0.5 * span;
    xMax = real[1] + 0.5 * span;
  } else {
    const a = Math.abs(toNumberR(solution.a));
    const half = Math.max(2, 2 * Math.sqrt(Math.max(Math.abs(k), 1) / a));
    xMin = h - half;
    xMax = h + half;
  }
  const ys = [f(xMin), f(xMax), k, 0];
  const [yMin, yMax] = padRange(Math.min(...ys), Math.max(...ys), 0.08, 1e-6);
  return { xMin, xMax, yMin, yMax };
}
